<script setup>
import {
  Dialog,
  DialogOverlay,
  DialogPanel,
  Tab,
  TabGroup,
  TabList,
  TabPanel,
  TabPanels,
  TransitionChild,
  TransitionRoot
} from "@headlessui/vue";
import {Icon} from "@iconify/vue";
import {computed, reactive, ref} from "vue";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import {required} from '@vuelidate/validators';
import {useVuelidate} from '@vuelidate/core';
import {useUserStore} from "@/stores/user.js";
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';
import AutoComplete from 'primevue/autocomplete';
import {Button, InputGroup, InputGroupAddon, InputText, Textarea} from "primevue";
import Attachments from "@/views/orders/Details/Attachments.vue";
import {gregorianDate} from "@/utils/gregorianDate.js";

const emit = defineEmits(['closeModal', 'addedNewHotOrder']);
const props = defineProps({
  orderID: Number,
  orderTitle: String
})

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)

/* attachments */
const attachments = ref(null)

/* Auxiliary keys allowed for all fields */
const helperKeys = [
  'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
  'ArrowLeft', 'ArrowRight', 'Home', 'End'
]
/* ---------- Stores ---------- */
const ordersStore = useMainOrdersStore();
const userStore = useUserStore();

/* ---------- Loading status ---------- */
const loading = ref(false)
const saving = ref(false)
const isUploadingAttachment = ref(false)

const acRef = ref(null)
//const isShowAddTakerModal = ref(false);
const selectedTaker = ref({})
const serverUsers = ref([])
const users = computed(() => serverUsers.value || [])
const states = ['New', 'Delivering', 'Delivered', 'Canceled', 'Paid']
const stateColorsMap = {
  New: 'bg-[#9E9E9E]',
  Delivering: 'bg-[#FF9800]',
  Delivered: 'bg-[#2196F3]',
  Canceled: 'bg-[#F44336]',
  Paid: 'bg-[#4CAF50]',
}

const hotOrder = reactive({
  "orderID": props.orderID,
  "state": "New",
  "title": props.orderTitle,
  "price": null,
  "takerID": "",
  "description": "",
  "notes": "",
})
const rules = computed(() => {
  return {
    state: {required},
    title: {required},
    price: {required},
    takerID: {required}
  };
});
const $vulidate = useVuelidate(rules, hotOrder);

function closeModal() {
  emit('closeModal');
}

/*async function selectTaker(userID) {
  await userStore.action_getAllUsers()
  hotOrder.takerID = userID
}

function showAddTakerModal() {
  isShowAddTakerModal.value = true
}

function closeAddTakerModal() {
  isShowAddTakerModal.value = false
}*/

/* ---------- Initial fetch ---------- */
/*async function fetchInitialData() {
  loading.value = true
  await userStore.action_getAllUsers()
  loading.value = false
}*/

/* Object filter: Keep only keys that have the value truthy */
function buildPayload() {
  const payload = {}
  Object.entries(hotOrder).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) payload[k] = v
  })
  if (attachments.value.length) {
    payload.attachments = {paymentReceipts: attachments.value}
  }
  return payload
}

async function onSave() {
  // Validation (if Vuelidate is available)
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  // 2) Filter filled fields
  const payload = buildPayload()
  if (Object.keys(payload).length === 0) {
    showMessage('No fields are filled in.', 'warning')
    return
  }

  //const ok = await showAlert('You are adding new hotOrder?', 'Are you sure?', 'Yes', 'No')
  //if (!ok) return

  // API call
  try {
    saving.value = true
    const res = await ordersStore.action_addHotOrder_currencySell(payload)
    if (res === true) {
      showMessage('New Taker added!', 'success')
      emit('addedNewHotOrder')
    } else {
      showMessage(res, 'error')
    }
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    saving.value = false
  }
}

function allowDigitsDot(e) {
  if (
      helperKeys.includes(e.key) ||
      (e.ctrlKey || e.metaKey)
  ) return

  if (e.key === '.') {
    if (e.target.value.includes('.')) e.preventDefault()
    return
  }

  if (!/^[0-9]$/.test(e.key)) e.preventDefault()
}

async function onFilter(event) {
  const keyword = event.query?.trim()
  if (!keyword || keyword.length < 3) {
    serverUsers.value = []
    loading.value = false
    return
  }

  loading.value = true
  try {
    const result = await userStore.action_searchAccounts({keyword})
    serverUsers.value = result || []
  } finally {
    loading.value = false
  }
}

async function changeTaker(event) {
  hotOrder.takerID = selectedTaker.value.id
}

function getDiscordCreationDate(entityId) {
  if (!entityId) return null;
  const TIMESTAMP_OFFSET = 22n;
  const DISCORD_EPOCH = 1420070400000n;
  const id = BigInt(entityId);
  const timestamp = (id >> TIMESTAMP_OFFSET) + DISCORD_EPOCH;
  return gregorianDate(Number(timestamp), 'HH:mm - DD MMM YYYY');
}

const optionLabel = (item) => {
  if (!item) return ''; // جلوگیری از خطا
  if (item.discordDisplayName && item.discordUsername) {
    return `${item.discordDisplayName} - ${item.discordUsername}`;
  }
  return item.name ?? '';
};
</script>

<template>
  <TransitionRoot appear show as="template">
    <Dialog as="div" @close="closeModal" class="relative z-[999]">
      <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-[black]/60"/>
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex h-full overflow-hidden items-center justify-center px-4 py-8">
          <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
          >
            <DialogPanel
                class="w-full flex flex-col overflow-hidden rounded-md max-w-2xl h-auto text-left align-middle shadow-xl transition-all bg-white dark:bg-[#0e1726] text-black-600 dark:text-white">
              <div class="h-full flex flex-col">
                <div class="flex flex-row gap-4 justify-between px-4 border-b border-slate-700">
                  <div class="flex-1 flex flex-row justify-between items-center">
                    <div class="text-sm py-3">
                      New Taker
                    </div>
                    <div class="flex flex-col gap-0.5">
                      <FloatLabel class="w-full custom-select"
                                  :class="{ 'has-error': $vulidate.state.$error}"
                                  variant="on">
                        <Select v-model="hotOrder.state" inputId="state" :options="states"
                                class="w-full min-w-28 !bg-transparent h-[28px] 2xl:h-[32px] !text-xs 2xl:!text-sm"
                                :class="[$vulidate.state.$error ? '!border !border-red-300' : '!border-slate-700']"
                                input-class="!py-1 !items-center !flex !pr-0"
                                overlay-class="select-overlay-in-modal h-max">
                          <template #value="slotProps">
                            <div class="flex items-center gap-2 !text-xs">
                              <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.value]"></span>
                              {{ slotProps.value }}
                            </div>
                          </template>
                          <template #option="slotProps">
                            <div class="flex items-center gap-2 !text-xs 2xl:!text-sm">
                              <span class="w-2 h-2 rounded-full" :class="stateColorsMap[slotProps.option]"></span>
                              {{ slotProps.option }}
                            </div>
                          </template>
                        </Select>
                      </FloatLabel>
                      <template v-if="$vulidate.state.$error">
                        <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                           v-if="!$vulidate.state.required.$response">
                          This field is required
                        </p>
                      </template>
                    </div>
                  </div>
                  <div class="flex flex-row items-center gap-4">
                    <div class="h-full w-[1px] bg-slate-700"></div>
                    <button
                        type="button"
                        class="text-white-dark outline-none hover:text-red-500"
                        @click="closeModal"
                    >
                      <Icon icon="pajamas:close-xs"/>
                    </button>
                  </div>
                </div>
                <div class="p-4 flex-1 overflow-auto h-full flex flex-col gap-6">
                  <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
                    <div class="space-y-4 text-white/80 text-sm h-full">
                      <!-- Hot Order details form -->
                      <TabPanels class="h-full">
                        <div class="grid grid-cols-5 gap-2 h-full">
                          <div class="col-span-5 flex flex-col gap-2 2xl:gap-4 relative h-full overflow-hidden">
                            <div class="flex flex-row gap-2 items-center justify-between">
                              <div class="flex flex-row gap-2 items-center">
                                <TabList class="">
                                  <Tab
                                      v-slot="{ selected }"
                                      as="template"
                                      v-for="(item, i) in tabs"
                                      :key="i"
                                  >
                                    <button
                                        :class="[
                            selected
                              ? 'text-white bg-primary-700'
                              : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                          ]"
                                        class="text-xs font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-4 py-1 transition duration-150 rounded-none first:rounded-l-lg last:rounded-r-lg border-r last:border-r-0 border-slate-500"
                                    >
                                      {{ item.title }}
                                    </button>
                                  </Tab>
                                </TabList>
                              </div>
                            </div>
                            <hr class="border-0 h-0.5 bg-slate-800">
                            <TabPanel :unmount="false"
                                      class="col-span-4 flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-2">
                              <div class="space-y-4">
                                <div class="grid grid-cols-12 gap-4">
                                  <div class="col-span-8 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                                    <FloatLabel variant="on">
                                      <InputText id="hotOrderTitle" v-model="hotOrder.title" autocomplete="off"
                                                 class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                                 :class="{ 'has-error': $vulidate.title.$error }"/>
                                      <label for="hotOrderTitle" class="!bg-slate-900"
                                             :class="{ 'focus:text-red-300': $vulidate.title.$error }">Title</label>
                                    </FloatLabel>
                                    <template v-if="$vulidate.title.$error">
                                      <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                                         v-if="!$vulidate.title.required.$response">
                                        This field is required
                                      </p>
                                    </template>
                                  </div>
                                  <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                                    <InputGroup>
                                      <FloatLabel variant="on"
                                                  :class="{ 'has-error': $vulidate.price.$error }">
                                        <InputText id="hotOrderPrice" v-model="hotOrder.price" autocomplete="off"
                                                   class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                                   :class="{ 'has-error has-addon': $vulidate.price.$error }"
                                                   @keydown="allowDigitsDot"/>
                                        <label for="hotOrderPrice" class="!bg-slate-900"
                                               :class="{ 'has-error': $vulidate.price.$error }">Price</label>
                                      </FloatLabel>
                                      <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$
                                      </InputGroupAddon>
                                    </InputGroup>
                                    <template v-if="$vulidate.price.$error">
                                      <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                                         v-if="!$vulidate.price.required.$response">
                                        This field is required
                                      </p>
                                    </template>
                                  </div>
                                </div>
                                <div class="grid grid-cols-12 gap-4">
                                  <div class="col-span-12 flex flex-row gap-2 text-xs 2xl:text-sm">
                                    <div class="flex-1 flex flex-col gap-0.5">
                                      <FloatLabel class="w-full custom-select"
                                                  :class="{ 'has-error': $vulidate.takerID.$error}"
                                                  variant="on">
                                        <AutoComplete ref="acRef" v-model="selectedTaker" input-id="takerID"
                                                      :spellcheck="false"
                                                      :suggestions="serverUsers" forceSelection
                                                      @complete="onFilter" completeOnFocus
                                                      :optionLabel="optionLabel"
                                                      @itemSelect="changeTaker"
                                                      :minLength="3"
                                                      :delay="1000"
                                                      :loading="loading"
                                                      class="w-full !rounded-md"
                                                      :class="[$vulidate.takerID.$error ? '!border !border-red-300' : '']"
                                                      input-class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !text-xs 2xl:!text-sm !border-slate-700"
                                                      overlay-class="!bg-[#0F172A] !rounded-md"
                                                      append-to="self"
                                                      :virtualScrollerOptions="{ itemSize: 48 }"
                                                      dropdown>
                                          <template #option="slotProps">
                                            <div class="flex items-center gap-2 text-sm">
                                              <img v-if="slotProps.option.discordAvatar"
                                                   :alt="slotProps.option.name"
                                                   :src="slotProps.option.discordAvatar"
                                                   class="size-10 rounded-full border border-slate-700"/>
                                              <div v-else
                                                   class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                     viewBox="0 0 24 24"
                                                     fill="none" stroke="currentColor" stroke-width="2"
                                                     stroke-linecap="round"
                                                     stroke-linejoin="round"
                                                     class="lucide lucide-user-icon lucide-user">
                                                  <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                                                  <circle cx="12" cy="7" r="4"/>
                                                </svg>
                                              </div>
                                              <span>
                                                {{ slotProps.option.discordDisplayName ?? slotProps.option.name }}
                                              </span>
                                              <span class="text-sm text-slate-400">
                                                {{ slotProps.option.discordUsername ?? '' }}
                                              </span>
                                            </div>
                                          </template>
                                        </AutoComplete>
                                        <label for="takerID" class="!bg-slate-900">Taker</label>
                                      </FloatLabel>
                                      <template v-if="$vulidate.takerID.$error">
                                        <p class="text-red-300 mt-0.5 2xl:mt-1 text-[.6rem] 2xl:text-xs"
                                           v-if="!$vulidate.takerID.required.$response">
                                          This field is required
                                        </p>
                                      </template>
                                    </div>
<!--                                    <button
                                        class="btn btn-outline-dark py-0 px-2 text-xs 2xl:text-sm font-normal h-[32px] 2xl:h-[40px]"
                                        @click="showAddTakerModal">
                                      Add Taker
                                    </button>-->
                                  </div>
                                  <div v-if="selectedTaker?.id"
                                       class="col-span-12 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md p-2">
                                    <img v-if="selectedTaker.discordAvatar" :src="selectedTaker.discordAvatar"
                                         alt="avatar" class="size-10 rounded-full border border-slate-700">
                                    <div v-else
                                         class="flex items-center justify-center size-10 rounded-full border border-slate-700">
                                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                           fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                           stroke-linejoin="round" class="lucide lucide-user-icon lucide-user">
                                        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                      </svg>
                                    </div>
                                    <div class="flex-1 grid grid-cols-2 gap-1">
                                      <div class="flex flex-col">
                                        <div>{{ selectedTaker.discordDisplayName ?? selectedTaker.name }}</div>
                                        <div class="text-slate-400">{{ selectedTaker.discordUsername ?? "-" }}</div>
                                      </div>
                                      <div class="flex flex-col">
                                        <div>{{ selectedTaker.verificationState ?? "-" }}</div>
                                        <div>{{ getDiscordCreationDate(selectedTaker.discordID) ?? "-" }}</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                                    <FloatLabel variant="on">
                          <Textarea id="description" v-model="hotOrder.description" rows="5" style="resize: none"
                                    class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
                                      <label for="description" class="!bg-slate-900">Description</label>
                                    </FloatLabel>
                                  </div>
                                  <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                                    <FloatLabel variant="on">
                          <Textarea id="notes" v-model="hotOrder.notes" rows="5" style="resize: none"
                                    class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700"/>
                                      <label for="notes" class="!bg-slate-900">Notes</label>
                                    </FloatLabel>
                                  </div>
                                </div>
                              </div>
                            </TabPanel>
                            <TabPanel :unmount="false"
                                      class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2">
                              <!-- Only changes -->
                              <Attachments
                                  id="hotOrder"
                                  :count="16"
                                  :only-changes="true"
                                  size-class="w-24 h-24"
                                  font-size="text-[.6rem]"
                                  v-model:uploading="isUploadingAttachment"
                                  @update:payload="val => (attachments = val)"
                              />
                            </TabPanel>
                          </div>
                        </div>
                      </TabPanels>
                    </div>
                  </TabGroup>
                </div>
                <div class="px-3 pb-3 flex justify-end gap-3">
                  <button @click="onSave" class="btn btn-outline-primary py-1 flex flex-row gap-2 px-2"
                          :disabled="saving">
                    <Icon v-if="saving" icon="eos-icons:bubble-loading" height="24"/>
                    Save
                  </button>
                </div>
              </div>
<!--              <AddTaker v-if="isShowAddTakerModal" @closeModal="closeAddTakerModal" @addNewTaker="selectTaker"/>-->
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style>
@import "@/assets/primevue.css";
</style>
<style>

</style>
